import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { LayoutDashboard, Users, IndianRupee, Settings, LogOut, Menu, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/context/AuthContext';
import { useState } from 'react';

// Logo URL
const LOGO_URL = "https://customer-assets.emergentagent.com/job_gym-roster-pro-1/artifacts/2uba3mjm_goat.jpg";

const DashboardLayout = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const navItems = [
    { path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard', exact: true },
    { path: '/dashboard/members', icon: Users, label: 'Members' },
    { path: '/dashboard/payments', icon: IndianRupee, label: 'Payments' },
    { path: '/dashboard/settings', icon: Settings, label: 'Settings' }
  ];

  const NavItem = ({ item }) => (
    <NavLink
      to={item.path}
      end={item.exact}
      onClick={() => setSidebarOpen(false)}
      className={({ isActive }) =>
        `flex items-center gap-3 px-4 py-3 transition-all duration-200 ${
          isActive
            ? 'bg-[#FF3B30]/10 border-l-2 border-[#FF3B30] text-[#FF3B30]'
            : 'text-zinc-400 hover:text-white hover:bg-zinc-900/50 border-l-2 border-transparent'
        }`
      }
    >
      <item.icon className="w-5 h-5" />
      <span className="font-['Inter'] text-sm uppercase tracking-wider font-medium">{item.label}</span>
    </NavLink>
  );

  return (
    <div className="min-h-screen bg-[#050505] flex">
      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed lg:static inset-y-0 left-0 z-50
        w-64 bg-[#0A0A0A] border-r border-zinc-800
        transform transition-transform duration-300 ease-in-out
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between p-4 border-b border-zinc-800">
            <img 
              src={LOGO_URL} 
              alt="G.O.A.T Fitness" 
              className="h-10 w-auto"
            />
            <button 
              className="lg:hidden text-zinc-400 hover:text-white"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 py-4" data-testid="dashboard-nav">
            {navItems.map((item) => (
              <NavItem key={item.path} item={item} />
            ))}
          </nav>

          {/* User Section */}
          <div className="p-4 border-t border-zinc-800">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-[#FF3B30]/20 rounded-full flex items-center justify-center">
                <span className="font-['Barlow_Condensed'] font-bold text-[#FF3B30]">
                  {user?.name?.charAt(0).toUpperCase() || 'O'}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-['Inter'] text-white text-sm font-medium truncate">
                  {user?.name || 'Owner'}
                </p>
                <p className="text-zinc-500 text-xs truncate">{user?.email}</p>
              </div>
            </div>
            <Button
              data-testid="logout-btn"
              onClick={handleLogout}
              variant="ghost"
              className="w-full justify-start gap-3 text-zinc-400 hover:text-white hover:bg-zinc-900/50 rounded-none"
            >
              <LogOut className="w-5 h-5" />
              <span className="text-sm uppercase tracking-wider">Logout</span>
            </Button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-h-screen">
        {/* Mobile Header */}
        <header className="lg:hidden sticky top-0 z-30 bg-[#0A0A0A] border-b border-zinc-800 p-4">
          <div className="flex items-center justify-between">
            <button 
              data-testid="mobile-menu-btn"
              onClick={() => setSidebarOpen(true)}
              className="text-zinc-400 hover:text-white"
            >
              <Menu className="w-6 h-6" />
            </button>
            <img 
              src={LOGO_URL} 
              alt="G.O.A.T Fitness" 
              className="h-8 w-auto"
            />
            <div className="w-6" /> {/* Spacer for centering */}
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 p-4 lg:p-8 overflow-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default DashboardLayout;
